import { useState } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  CardContent,
} from "@mui/material";
import type { Factoring, FactoringEntry } from "components/factoringModel";
import { getJaDisplayName } from "components/factoringUtil";
import { updateFactoringEntryDetail } from "./updateFactoringEntryDetail";

interface Props {
  factoring: Factoring;
  refresh: () => void;
}

export default function FactoringEntryComponent(props: Props) {
  const {
    factoring: initialFactoring,
    refresh,
  } = props;

  const [entries, setEntries] = useState<FactoringEntry[]>(initialFactoring.entries || []);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Toast state
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Handle form field changes for a specific entry
  const handleEntryChange = (index: number, field: keyof FactoringEntry, value: any) => {
    const newEntries = [...entries];
    newEntries[index] = {
      ...newEntries[index],
      [field]: value,
    };
    setEntries(newEntries);
  };

  // Handle update button click
  const handleUpdate = async () => {
    if (!window.confirm("申込情報を更新しますか？")) {
      return;
    }

    setIsLoading(true);
    try {
      await updateFactoringEntryDetail({
        userId: initialFactoring.userId,
        factoringId: initialFactoring.id,
        entries: entries,
      });
      refresh();
      setShowSuccessToast(true);
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "申込情報の更新中にエラーが発生しました。"
      );
      setShowErrorToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Toast close handlers
  const handleCloseSuccessToast = () => {
    setShowSuccessToast(false);
  };

  const handleCloseErrorToast = () => {
    setShowErrorToast(false);
  };

  return (
    <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          請求書情報
        </Typography>

        {entries.length === 0 ? (
          <Typography variant="body1" sx={{ my: 4, textAlign: 'center' }}>
            請求書がありません。
          </Typography>
        ) : (
          entries.map((entry, index) => (
            <Card key={index} sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    請求書情報 #{index + 1}
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={getJaDisplayName("companyName")}
                      value={entry.companyName}
                      onChange={(e) => handleEntryChange(index, "companyName", e.target.value)}
                      margin="normal"
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={getJaDisplayName("corporateNumber")}
                      value={entry.corporateNumber}
                      onChange={(e) => handleEntryChange(index, "corporateNumber", e.target.value)}
                      margin="normal"
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={getJaDisplayName("postalCode")}
                      value={entry.postalCode}
                      onChange={(e) => handleEntryChange(index, "postalCode", e.target.value)}
                      margin="normal"
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={getJaDisplayName("prefecture")}
                      value={entry.prefecture}
                      onChange={(e) => handleEntryChange(index, "prefecture", e.target.value)}
                      margin="normal"
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={getJaDisplayName("city")}
                      value={entry.city}
                      onChange={(e) => handleEntryChange(index, "city", e.target.value)}
                      margin="normal"
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={getJaDisplayName("address1")}
                      value={entry.address1}
                      onChange={(e) => handleEntryChange(index, "address1", e.target.value)}
                      margin="normal"
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={getJaDisplayName("address2")}
                      value={entry.address2}
                      onChange={(e) => handleEntryChange(index, "address2", e.target.value)}
                      margin="normal"
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      required
                      margin="normal"
                      label={getJaDisplayName("billingAmount")}
                      value={entry.billingAmount}
                      onChange={(e) => {
                        const onlyDigits = e.target.value.replace(/\D+/g, "");
                        const num = onlyDigits ? Number(onlyDigits) : 0;
                        handleEntryChange(index, "billingAmount", num);
                      }}
                      slotProps={{
                        input: {
                          endAdornment: <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>万円</Typography>,
                        },
                        htmlInput: {
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          min: 0,
                          max: 9999999999
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      required
                      margin="normal"
                      label={getJaDisplayName("paymentDate")}
                      value={entry.paymentDate}
                      onChange={(e) => handleEntryChange(index, "paymentDate", e.target.value)}
                      type="date"
                      slotProps={{
                        inputLabel: {
                          shrink: true
                        },
                        htmlInput: {
                          min: "1900-01-01",
                          max: "9999-12-31"
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={getJaDisplayName("isInvoiceIssued")}
                      value={entry.isInvoiceIssued ? "請求済" : "未請求"}
                      margin="normal"
                      slotProps={{
                        input: {
                          readOnly: true,
                        },
                      }}
                      disabled={isLoading}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))
        )}

        {/* Update Button */}
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdate}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? "更新中..." : "更新"}
          </Button>
        </Box>
      </Paper>

      {/* Success Toast */}
      <Snackbar
        open={showSuccessToast}
        autoHideDuration={6000}
        onClose={handleCloseSuccessToast}
      >
        <Alert
          onClose={handleCloseSuccessToast}
          severity="success"
          sx={{ width: "100%" }}
        >
          エントリー情報を正常に更新しました。
        </Alert>
      </Snackbar>

      {/* Error Toast */}
      <Snackbar
        open={showErrorToast}
        autoHideDuration={6000}
        onClose={handleCloseErrorToast}
      >
        <Alert
          onClose={handleCloseErrorToast}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
